import { useStoryManagerView } from '@src/stores-v2/StoreContext';
import { observer } from 'mobx-react-lite';
import { createContext, useContext, useMemo } from 'react';

import type { ReactNode } from 'react';

export interface StorySelection {
  selectedIds: ReadonlySet<string>;
  size: number;
  isSelected: (storyId: string) => boolean;
  toggle: (storyId: string) => void;
  clear: () => void;
}

export interface FolderViewActions {
  openMoveDialog: (storyIds: string[]) => void;
  openBulkDeleteDialog: (storyIds: string[]) => void;
}

const StorySelectionContext = createContext<StorySelection | null>(null);
const FolderViewActionsContext = createContext<FolderViewActions | null>(null);

export function useStorySelection(): StorySelection {
  const value = useContext(StorySelectionContext);
  if (!value) {
    throw new Error(
      'useStorySelection must be used inside <StoryFolderViewProvider>',
    );
  }
  return value;
}

export function useFolderViewActions(): FolderViewActions {
  const value = useContext(FolderViewActionsContext);
  if (!value) {
    throw new Error(
      'useFolderViewActions must be used inside <StoryFolderViewProvider>',
    );
  }
  return value;
}

type Props = {
  buildActions: (selection: StorySelection) => FolderViewActions;
  children: ReactNode;
};

export const StoryFolderViewProvider = observer((props: Props) => {
  const { buildActions, children } = props;
  // Bulk selection lives in the view store so checked rows survive navigating
  // away and back (and reset per org). See StoryManagerViewStore.
  const view = useStoryManagerView();
  const selectedIds = view.selectedStoryIds;

  const selection = useMemo<StorySelection>(
    () => ({
      selectedIds,
      size: selectedIds.size,
      isSelected: id => selectedIds.has(id),
      toggle: view.toggleStorySelection,
      clear: view.clearStorySelection,
    }),
    [selectedIds, view],
  );

  const actions = useMemo(
    () => buildActions(selection),
    [buildActions, selection],
  );

  return (
    <FolderViewActionsContext.Provider value={actions}>
      <StorySelectionContext.Provider value={selection}>
        {children}
      </StorySelectionContext.Provider>
    </FolderViewActionsContext.Provider>
  );
});
